/**
 * ImageCarousel — "Same geometry, different material" (CX-2729 Digital Twins).
 *
 * A fixed product scene rendered with a different material per tab: a large
 * main frame shows the active item's image; a row of chip+label tabs below it
 * selects which material is applied. The first item is active by default
 * (Jira AC). Prev/next arrows on the main frame cycle through items so the
 * carousel is usable without the tab row.
 */

import { useState } from "react";
import type { ImageCarousel as ImageCarouselData } from "../chapter.types";
import "./ImageCarousel.css";

interface ImageCarouselProps {
  title: string;
  carousel: ImageCarouselData;
}

const ChevronIcon = ({ direction }: { direction: "left" | "right" }) => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 14 14"
    fill="none"
    aria-hidden="true"
  >
    <path
      d={direction === "left" ? "M9 3 5 7l4 4" : "M5 3l4 4-4 4"}
      stroke="#fff"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export const ImageCarousel = ({ title, carousel }: ImageCarouselProps) => {
  const { items } = carousel;
  const [active, setActive] = useState(0);

  if (items.length === 0) {
    return null;
  }

  const goTo = (index: number) =>
    setActive((index + items.length) % items.length);

  return (
    <section className="cmf-carousel" aria-label={title}>
      <h3 className="cmf-carousel__title">{title}</h3>

      {/* Main frame — cross-faded images, one per item */}
      <div className="cmf-carousel__frame">
        {items.map((item, index) => (
          <img
            key={item.label}
            src={item.image}
            alt={item.label}
            className={`cmf-carousel__image${
              index === active ? " cmf-carousel__image--active" : ""
            }`}
            loading={index === active ? "eager" : "lazy"}
            draggable={false}
          />
        ))}

        {items.length > 1 && (
          <>
            <button
              type="button"
              className="cmf-carousel__nav cmf-carousel__nav--prev"
              onClick={() => goTo(active - 1)}
              aria-label="Previous material"
            >
              <ChevronIcon direction="left" />
            </button>
            <button
              type="button"
              className="cmf-carousel__nav cmf-carousel__nav--next"
              onClick={() => goTo(active + 1)}
              aria-label="Next material"
            >
              <ChevronIcon direction="right" />
            </button>
          </>
        )}
      </div>

      {/* Tab selector — chip + label per item */}
      <div className="cmf-carousel__tabs" role="tablist">
        {items.map((item, index) => (
          <button
            key={item.label}
            type="button"
            role="tab"
            aria-selected={index === active}
            className={`cmf-carousel__tab${
              index === active ? " cmf-carousel__tab--active" : ""
            }`}
            onClick={() => setActive(index)}
          >
            <img
              src={item.chip ?? item.image}
              alt=""
              className="cmf-carousel__chip"
              draggable={false}
            />
            <span className="cmf-carousel__label">{item.label}</span>
          </button>
        ))}
      </div>
    </section>
  );
};
